import React, { useEffect } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';
import ErrorScreen from './ErrorScreen';

const CHUNK_RELOAD_KEY = 'chunk-error-reloaded';

// A lazy-loaded page file could not be downloaded — usually because a new
// version was deployed (old chunk names no longer exist) or the network dropped.
const isChunkLoadError = (error) =>
  error?.name === 'ChunkLoadError' ||
  /Loading (CSS )?chunk [\w-]+ failed/i.test(error?.message || '') ||
  /Failed to fetch dynamically imported module/i.test(error?.message || '');

const readReloadFlag = () => {
  try { return sessionStorage.getItem(CHUNK_RELOAD_KEY) === '1'; } catch { return true; }
};
const writeReloadFlag = (value) => {
  try {
    if (value) sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
    else sessionStorage.removeItem(CHUNK_RELOAD_KEY);
  } catch { /* storage blocked — skip */ }
};

// errorElement for every route: replaces React Router's developer error screen.
function RouteErrorPage() {
  const error = useRouteError();
  const chunkError = isChunkLoadError(error);
  const shouldAutoReload = chunkError && !readReloadFlag();

  useEffect(() => {
    console.error('Route error', error);
    if (shouldAutoReload) {
      // Reload once to pick up the latest files; the flag prevents a reload loop.
      writeReloadFlag(true);
      window.location.reload();
    } else if (!chunkError) {
      writeReloadFlag(false);
    }
  }, [error, chunkError, shouldAutoReload]);

  if (shouldAutoReload) return null;

  if (chunkError) {
    return (
      <ErrorScreen
        title="A new version is available"
        message="We just made some improvements to the app. Please refresh the page to get the latest version — it only takes a second."
      />
    );
  }

  if (isRouteErrorResponse(error) && error.status === 404) {
    return (
      <ErrorScreen
        title="We can't find that page"
        message="The link may be old or typed incorrectly. Please go back to the home page and try again from there."
      />
    );
  }

  return <ErrorScreen />;
}

export default RouteErrorPage;
